import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight, Expand } from "lucide-react";
import { GALLERY } from "../lib/data";

export default function Referenzen() {
  const [index, setIndex] = useState(null);
  const open = index !== null;

  useEffect(() => {
    if (open && window.lenis) window.lenis.stop();
    else if (window.lenis) window.lenis.start();
    const onKey = (e) => {
      if (!open) return;
      if (e.key === "Escape") setIndex(null);
      if (e.key === "ArrowRight") setIndex((i) => (i + 1) % GALLERY.length);
      if (e.key === "ArrowLeft") setIndex((i) => (i - 1 + GALLERY.length) % GALLERY.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <section id="referenzen" className="relative py-24 md:py-32 bg-ink" data-testid="referenzen-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <div>
            <div className="flex items-center gap-3 mb-5">
              <span className="h-px w-10 bg-volt" />
              <span className="text-xs md:text-sm font-bold tracking-[0.25em] uppercase text-volt">Referenzen</span>
            </div>
            <motion.h2 initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}
              className="font-heading font-black uppercase tracking-tighter text-4xl md:text-6xl text-white leading-[0.95]">
              Projekte in <span className="text-volt">Präzision</span>
            </motion.h2>
          </div>
          <p className="max-w-sm text-white/60 font-medium md:text-right">
            Ein Einblick in unsere Arbeit – von sauberen Kernbohrungen bis zur vollständigen Elektrovorbereitung.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 auto-rows-[220px] lg:auto-rows-[260px] gap-4">
          {GALLERY.map((g, i) => (
            <motion.button key={i} data-testid={`gallery-item-${i}`} onClick={() => setIndex(i)} aria-label={`${g.title} vergrößern`}
              initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.06 }}
              className={`group relative overflow-hidden rounded-2xl border border-white/[0.06] text-left cursor-pointer ${g.span}`}>
              <img src={g.url} alt={`${g.title} – VoltCore Elektro`} loading="lazy" decoding="async"
                className="w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-110" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent opacity-70 group-hover:opacity-90 transition-opacity" />
              <div className="absolute inset-0 border-2 border-volt/0 group-hover:border-volt/60 rounded-2xl transition-all duration-500" />
              <span className="absolute top-4 right-4 grid place-items-center h-10 w-10 rounded-full bg-black/50 backdrop-blur-md text-volt opacity-0 group-hover:opacity-100 translate-y-1 group-hover:translate-y-0 transition-all duration-400">
                <Expand className="h-4 w-4" />
              </span>
              <div className="absolute bottom-0 left-0 p-6 translate-y-2 group-hover:translate-y-0 transition-transform duration-500">
                <span className="text-[0.65rem] font-bold tracking-[0.2em] uppercase text-volt">VoltCore</span>
                <h3 className="font-heading font-bold text-white text-xl mt-1">{g.title}</h3>
              </div>
            </motion.button>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div data-testid="lightbox" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setIndex(null)}
            className="fixed inset-0 z-[100] grid place-items-center bg-black/92 backdrop-blur-md p-4 md:p-10">
            <button data-testid="lightbox-close" onClick={() => setIndex(null)} aria-label="Schließen"
              className="absolute top-5 right-5 grid place-items-center h-12 w-12 rounded-full bg-white/10 hover:bg-volt hover:text-black text-white transition-colors">
              <X className="h-5 w-5" />
            </button>
            <button data-testid="lightbox-prev" aria-label="Vorheriges Bild"
              onClick={(e) => { e.stopPropagation(); setIndex((i) => (i - 1 + GALLERY.length) % GALLERY.length); }}
              className="absolute left-3 md:left-8 grid place-items-center h-12 w-12 rounded-full bg-white/10 hover:bg-volt hover:text-black text-white transition-colors">
              <ChevronLeft className="h-6 w-6" />
            </button>
            <button data-testid="lightbox-next" aria-label="Nächstes Bild"
              onClick={(e) => { e.stopPropagation(); setIndex((i) => (i + 1) % GALLERY.length); }}
              className="absolute right-3 md:right-8 grid place-items-center h-12 w-12 rounded-full bg-white/10 hover:bg-volt hover:text-black text-white transition-colors">
              <ChevronRight className="h-6 w-6" />
            </button>
            <motion.figure key={index} initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }} onClick={(e) => e.stopPropagation()}
              className="max-w-5xl w-full flex flex-col items-center">
              <img src={GALLERY[index].url} alt={`${GALLERY[index].title} – VoltCore Elektro`}
                className="max-h-[80vh] w-auto max-w-full rounded-xl object-contain shadow-2xl" />
              <figcaption className="mt-4 font-heading font-bold text-white text-lg">{GALLERY[index].title}</figcaption>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
